import dayjs from "dayjs";
import { CLASSFINDER_ENTITY_TYPES, ClassSchedule, ClassScheduleResponse, SchedulesResponse, ServiceSchedule, ServiceScheduleResponse } from "../classfinder.interfaces";
import { SCHEDULE_DATE_FORMAT } from "../classfinder.constants";

type AnySchedule = ClassSchedule | ServiceSchedule;
type ScheduleEntityType = (typeof CLASSFINDER_ENTITY_TYPES)[keyof typeof CLASSFINDER_ENTITY_TYPES];

const isObject = (value: unknown): value is object => {
    return typeof value === "object" && value !== null;
};

export const isClassScheduleResponse = (response: unknown): response is ClassScheduleResponse => {
    return isObject(response) && "class_schedules" in response;
};

export const isServiceScheduleResponse = (response: unknown): response is ServiceScheduleResponse => {
    return isObject(response) && "service_schedules" in response;
};

const hasErrorMetadata = (error: unknown): error is { name?: string; code?: string } => {
    return typeof error === "object" && error !== null;
};

export const isAbortLikeError = (error: unknown): boolean => {
    if (!hasErrorMetadata(error)) return false;
    return error.name === "AbortError" || error.code === "ERR_CANCELED";
};

const appendUniqueSlots = (existingSchedule: AnySchedule, incomingSchedule: AnySchedule) => {
    const existingSlotIds = new Set(existingSchedule.slots.map((slot) => slot.id));

    incomingSchedule.slots.forEach((newSlot) => {
        if (newSlot.id && !existingSlotIds.has(newSlot.id)) {
            existingSchedule.slots.push(newSlot);
        }
    });
};

export const createDateChunks = (
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    chunkSize = 3
): [dayjs.Dayjs, dayjs.Dayjs][] => {
    const dayDifference = end.diff(start, "day") + 1;
    const chunks: [dayjs.Dayjs, dayjs.Dayjs][] = [];

    for (let i = 0; i < dayDifference; i += chunkSize) {
        const chunkStart = start.add(i, "day");
        const possibleChunkEnd = chunkStart.add(chunkSize - 1, "day");
        const chunkEnd = possibleChunkEnd.isBefore(end) ? possibleChunkEnd : end;
        chunks.push([chunkStart, chunkEnd]);
    }

    return chunks;
};

export const getRangeKey = (start: dayjs.Dayjs, end: dayjs.Dayjs): string => {
    return `${start.format(SCHEDULE_DATE_FORMAT)}-${end.format(SCHEDULE_DATE_FORMAT)}`;
};

export const mergeSchedulesByEntityType = (
    previousSchedules: Map<number, ClassSchedule | ServiceSchedule>,
    responses: SchedulesResponse[],
    type: ScheduleEntityType): Map<number, ClassSchedule | ServiceSchedule> => {
    const mergedSchedules = new Map(previousSchedules);

    responses.forEach((response) => {
        if (type === CLASSFINDER_ENTITY_TYPES.CLASSES && isClassScheduleResponse(response)) {
            (response.class_schedules ?? []).forEach((schedule) => {
                const scheduleId = schedule.class?.id;
                if (!scheduleId) return;

                if (!mergedSchedules.has(scheduleId)) {
                    mergedSchedules.set(scheduleId, schedule);
                    return;
                }

                const existingSchedule = mergedSchedules.get(scheduleId);
                if (existingSchedule) {
                    appendUniqueSlots(existingSchedule, schedule);
                }
            });
        }

        if (type === CLASSFINDER_ENTITY_TYPES.SERVICES && isServiceScheduleResponse(response)) {
            (response.service_schedules ?? []).forEach((schedule) => {
                const scheduleId = schedule.service?.id;
                if (!scheduleId) return;

                if (!mergedSchedules.has(scheduleId)) {
                    mergedSchedules.set(scheduleId, schedule);
                    return;
                }

                const existingSchedule = mergedSchedules.get(scheduleId);
                if (existingSchedule) {
                    appendUniqueSlots(existingSchedule, schedule);
                }
            });
        }
    });

    return mergedSchedules;
};